/**
 * Profiles panel + editor (profiles mode).
 *
 * A profile = one sending account + its own template over a chosen subset of
 * the shared chat pool. This is how a user sets up "account 2 posts a different
 * text to the same chats" (#3) and transfers chats to another account, which
 * then joins them in the background (#2).
 */

import { AlertTriangle, Plus, Trash2, UserPlus, Users } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

import { Button, Card, Input, Sheet } from "@/design";
import { useAccounts } from "@/api/accounts";
import { useChats } from "@/api/chats";
import { useTemplates } from "@/api/templates";
import {
  useCreateProfile,
  useDeleteProfile,
  useProfiles,
  useUpdateProfile,
  type JoinStatus,
  type Profile,
} from "@/api/profiles";
import { cn } from "@/lib/cn";
import { haptic } from "@/lib/telegram";

const JOIN_LABEL: Record<JoinStatus, string> = {
  pending: "вступает…",
  joined: "вступил",
  manual_required: "вручную",
  failed: "ошибка",
  not_needed: "готово",
};

const JOIN_STYLE: Record<JoinStatus, string> = {
  pending: "bg-warning/15 text-warning",
  joined: "bg-success/15 text-success",
  manual_required: "bg-white/[0.06] text-text-muted",
  failed: "bg-danger/15 text-danger",
  not_needed: "bg-white/[0.06] text-text-muted",
};

export function ProfilesPanel() {
  const { data: profiles, isLoading } = useProfiles();
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState<Profile | null>(null);
  const del = useDeleteProfile();

  const openCreate = () => {
    haptic.select();
    setEditing(null);
    setEditorOpen(true);
  };
  const openEdit = (p: Profile) => {
    haptic.select();
    setEditing(p);
    setEditorOpen(true);
  };

  const handleDelete = async (p: Profile) => {
    haptic.impact("medium");
    try {
      await del.mutateAsync(p.id);
      toast.success("Профиль удалён");
    } catch (e) {
      toast.error((e as Error)?.message || "Не удалось удалить");
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between px-1">
        <div className="text-xs text-text-muted uppercase tracking-wider font-medium flex items-center gap-1.5">
          <Users className="size-3" />
          Профили рассылки
        </div>
        <button
          onClick={openCreate}
          className="text-xs text-accent hover:text-accent/80 flex items-center gap-0.5 transition-colors"
        >
          <Plus className="size-3.5" />
          Профиль
        </button>
      </div>

      {/* Ban-risk reminder: mailing from multiple accounts + auto-join is a
          classic flag vector. Keep it short but present. */}
      <div className="flex items-start gap-2 rounded-2xl bg-warning/[0.08] border border-warning/20 px-3 py-2.5">
        <AlertTriangle className="size-3.5 text-warning shrink-0 mt-0.5" />
        <div className="text-[11px] text-text-muted leading-relaxed">
          Рассылка с нескольких аккаунтов и авто-вступление в чаты повышают риск
          блокировки. Используйте разные тексты, щадящие интервалы и не спешите с
          новыми аккаунтами.
        </div>
      </div>

      {isLoading ? (
        <div className="px-4 py-8 text-center text-xs text-text-muted">Загрузка…</div>
      ) : (profiles?.length ?? 0) === 0 ? (
        <div className="rounded-2xl border border-white/[0.06] bg-white/[0.02] px-4 py-8 text-center text-xs text-text-muted">
          Пока нет профилей. Создайте профиль, чтобы рассылать с конкретного
          аккаунта своим текстом.
        </div>
      ) : (
        <div className="space-y-2">
          {profiles!.map((p) => (
            <ProfileCard
              key={p.id}
              profile={p}
              onEdit={() => openEdit(p)}
              onDelete={() => handleDelete(p)}
            />
          ))}
        </div>
      )}

      <ProfileEditorSheet
        open={editorOpen}
        profile={editing}
        onClose={() => setEditorOpen(false)}
      />
    </div>
  );
}

function ProfileCard({
  profile,
  onEdit,
  onDelete,
}: {
  profile: Profile;
  onEdit: () => void;
  onDelete: () => void;
}) {
  return (
    <Card padded={false} className="p-4">
      <div className="flex items-start justify-between gap-3">
        <button onClick={onEdit} className="flex-1 min-w-0 text-left">
          <div className="text-[14px] font-semibold truncate">
            {profile.name || `Профиль #${profile.id}`}
          </div>
          <div className="text-[11px] text-text-muted truncate mt-0.5">
            {profile.account_name ?? "аккаунт удалён"} ·{" "}
            {profile.template_name ?? "нет шаблона"} · {profile.interval_minutes}м
          </div>
        </button>
        <div className="flex items-center gap-1 shrink-0">
          <span
            className={cn(
              "text-[10px] px-1.5 py-0.5 rounded-md uppercase tracking-wide",
              profile.is_enabled
                ? "bg-success/15 text-success"
                : "bg-white/[0.06] text-text-dim",
            )}
          >
            {profile.is_enabled ? "вкл" : "выкл"}
          </span>
          <button
            onClick={onDelete}
            className="size-7 rounded-lg grid place-items-center text-text-dim hover:text-danger hover:bg-danger/10 transition-colors"
            aria-label="Удалить профиль"
          >
            <Trash2 className="size-3.5" />
          </button>
        </div>
      </div>

      {profile.chats.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-3">
          {profile.chats.map((c) => (
            <span
              key={c.chat_id}
              className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-lg bg-white/[0.04]"
              title={c.join_error ?? undefined}
            >
              <span className="truncate max-w-[120px]">{c.title}</span>
              <span
                className={cn(
                  "px-1 py-0.5 rounded text-[9px] uppercase tracking-wide",
                  JOIN_STYLE[c.join_status],
                )}
              >
                {JOIN_LABEL[c.join_status]}
              </span>
            </span>
          ))}
        </div>
      )}
    </Card>
  );
}

function ProfileEditorSheet({
  open,
  profile,
  onClose,
}: {
  open: boolean;
  profile: Profile | null;
  onClose: () => void;
}) {
  const { data: accounts } = useAccounts();
  const { data: templates } = useTemplates();
  const { data: chats } = useChats();
  const create = useCreateProfile();
  const update = useUpdateProfile();

  const isEdit = profile != null;
  const mailTemplates = (templates ?? []).filter((t) => t.kind === "mail");

  const [name, setName] = useState("");
  const [accountId, setAccountId] = useState<number | null>(null);
  const [templateId, setTemplateId] = useState<number | null>(null);
  const [interval, setInterval] = useState(40);
  const [selectedChats, setSelectedChats] = useState<Set<number>>(new Set());

  // Reset form to the target profile (or blank for create) whenever it opens.
  const [lastKey, setLastKey] = useState<string>("");
  const key = `${open}-${profile?.id ?? "new"}`;
  if (open && key !== lastKey) {
    setLastKey(key);
    setName(profile?.name ?? "");
    setAccountId(profile?.account_id ?? null);
    setTemplateId(profile?.template_id ?? null);
    setInterval(profile?.interval_minutes ?? 40);
    setSelectedChats(new Set(profile?.chats.map((c) => c.chat_id) ?? []));
  }

  const toggleChat = (id: number) => {
    haptic.select();
    setSelectedChats((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleSave = async () => {
    if (accountId == null) {
      toast.error("Выберите аккаунт");
      return;
    }
    haptic.impact("medium");
    try {
      if (isEdit) {
        await update.mutateAsync({
          id: profile.id,
          patch: {
            name,
            template_id: templateId,
            interval_minutes: interval,
          },
        });
        toast.success("Профиль обновлён");
      } else {
        await create.mutateAsync({
          account_id: accountId,
          template_id: templateId,
          name,
          interval_minutes: interval,
          chat_ids: [...selectedChats],
        });
        toast.success("Профиль создан — аккаунт начнёт вступать в чаты");
      }
      onClose();
    } catch (e) {
      toast.error((e as Error)?.message || "Не удалось сохранить");
    }
  };

  const saving = create.isPending || update.isPending;

  return (
    <Sheet
      open={open}
      onClose={onClose}
      title={isEdit ? "Профиль рассылки" : "Новый профиль"}
      description={
        isEdit
          ? "Измените текст, интервал или название"
          : "Аккаунт + свой текст поверх выбранных чатов"
      }
    >
      <div className="space-y-5">
        <Field label="Название">
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Например: Основной оффер"
          />
        </Field>

        <Field label="Аккаунт">
          {isEdit ? (
            <div className="text-[13px] text-text-muted px-1">
              {profile.account_name ?? "аккаунт удалён"} (нельзя изменить)
            </div>
          ) : (
            <div className="flex flex-wrap gap-2">
              {(accounts ?? [])
                .filter((a) => !a.is_banned)
                .map((a) => (
                  <Chip
                    key={a.id}
                    active={accountId === a.id}
                    onClick={() => {
                      haptic.select();
                      setAccountId(a.id);
                    }}
                  >
                    {a.name}
                  </Chip>
                ))}
            </div>
          )}
        </Field>

        <Field label="Шаблон (текст этого профиля)">
          <div className="flex flex-wrap gap-2">
            {mailTemplates.map((t) => (
              <Chip
                key={t.id}
                active={templateId === t.id}
                onClick={() => {
                  haptic.select();
                  setTemplateId(templateId === t.id ? null : t.id);
                }}
              >
                {t.name}
              </Chip>
            ))}
            {mailTemplates.length === 0 && (
              <div className="text-[12px] text-text-muted">
                Нет шаблонов рассылки — создайте на вкладке «Шаблоны».
              </div>
            )}
          </div>
        </Field>

        <Field label="Интервал между кругами (мин)">
          <Input
            type="number"
            min={1}
            max={1440}
            value={interval}
            onChange={(e) => setInterval(Math.max(1, Number(e.target.value) || 1))}
          />
        </Field>

        {!isEdit && (
          <Field label="Чаты для этого аккаунта">
            <div className="rounded-2xl border border-white/[0.06] bg-white/[0.02] max-h-[240px] overflow-y-auto divide-y divide-white/[0.04]">
              {(chats ?? []).length === 0 ? (
                <div className="px-4 py-6 text-center text-xs text-text-muted">
                  Сначала добавьте чаты в пул на вкладке «Чаты».
                </div>
              ) : (
                (chats ?? []).map((c) => (
                  <button
                    key={c.id}
                    onClick={() => toggleChat(c.id)}
                    className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-white/[0.03] transition-colors"
                  >
                    <div
                      className={cn(
                        "size-5 rounded-md border grid place-items-center shrink-0",
                        selectedChats.has(c.id)
                          ? "bg-accent border-accent"
                          : "border-white/20",
                      )}
                    >
                      {selectedChats.has(c.id) && (
                        <div className="size-2 rounded-sm bg-white" />
                      )}
                    </div>
                    <span className="text-[13px] truncate">{c.title}</span>
                  </button>
                ))
              )}
            </div>
            <div className="flex items-start gap-2 mt-2 px-1">
              <UserPlus className="size-3 text-text-dim shrink-0 mt-0.5" />
              <div className="text-[11px] text-text-dim leading-relaxed">
                Аккаунт вступит в публичные чаты автоматически. В приватные
                (t.me/c/…) нужно вступить вручную.
              </div>
            </div>
          </Field>
        )}

        <Button fullWidth size="lg" onClick={handleSave} loading={saving}>
          {isEdit ? "Сохранить" : "Создать профиль"}
        </Button>
      </div>
    </Sheet>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-2">
      <div className="text-[12px] text-text-muted font-medium px-1">{label}</div>
      {children}
    </div>
  );
}

function Chip({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className={cn(
        "px-3 py-2 rounded-xl text-[13px] font-medium transition-colors",
        active
          ? "bg-gradient-accent text-white"
          : "bg-white/[0.04] text-text-muted hover:bg-white/[0.07]",
      )}
    >
      {children}
    </button>
  );
}
